import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { UploadCloud, X, CheckCircle2, ChevronsRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import PageHero from "../components/PageHero";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CATEGORIES } from "../mock";
import { submitStory, errorMessage } from "../lib/api";

const initial = {
  firstName: "",
  lastName: "",
  email: "",
  bio: "",
  title: "",
  story: "",
  excerpt: "",
  category: "",
  agreePrivacy: false,
  agreeFeature: false,
};

const inputCls = "h-12 bg-white border-[#e4e2d6] focus-visible:ring-[#4BAF47] focus-visible:ring-offset-0 text-[#24231D] placeholder:text-[#a8a79f]";

export default function TellYourStory() {
  const [form, setForm] = useState(initial);
  const [image, setImage] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [hp, setHp] = useState("");
  const startedAt = useRef(Date.now());
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const handleFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please upload an image file.");
    if (file.size > 5 * 1024 * 1024) return toast.error("Image must be under 5MB.");
    const reader = new FileReader();
    reader.onload = () => setImage({ name: file.name, dataUrl: reader.result });
    reader.readAsDataURL(file);
  };

  const validate = () => {
    if (!form.firstName.trim()) return "Please enter your first name.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return "Please enter a valid email.";
    if (form.bio.length > 300) return "Author bio must be under 300 characters.";
    if (form.title.trim().length < 5) return "Please add a title (5+ characters).";
    if (form.story.trim().length < 50) return "Your story should be at least 50 characters.";
    if (!form.category) return "Please pick a category.";
    if (!form.agreePrivacy) return "You must agree to the Privacy Policy.";
    if (!form.agreeFeature) return "Please acknowledge that your story may be featured.";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) return toast.error(err);
    setSubmitting(true);
    try {
      const story = await submitStory({
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        email: form.email.trim(),
        bio: form.bio.trim(),
        title: form.title.trim(),
        story: form.story.trim(),
        excerpt: form.excerpt.trim(),
        category: form.category,
        image: image?.dataUrl || null,
        website: hp,
        started_at: startedAt.current,
      });
      setDone({ ...story, email: form.email.trim() });
      toast.success("Story submitted! Our editorial team will review it.");
    } catch (e2) {
      toast.error(errorMessage(e2, "Could not submit your story. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <>
        <PageHero eyebrow="Thank you" title="Your story has been submitted" />
        <section className="max-w-[800px] mx-auto px-5 py-20 text-center">
          <CheckCircle2 size={72} className="mx-auto text-[#4BAF47]" />
          <h2 className="text-3xl font-extrabold mt-6">“{done.title}” is under review</h2>
          <p className="text-[#878680] text-lg mt-4 leading-relaxed">
            Our editorial team reviews every submission. Once approved, your story will appear in Discover Stories and on our social channels. We will email <strong className="text-[#24231D]">{done.email}</strong> when it is live.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/stories" className="btn-green">Discover Stories <ChevronsRight size={16} /></Link>
            <button onClick={() => { setDone(null); setForm(initial); setImage(null); startedAt.current = Date.now(); }} className="btn-outline">Submit Another</button>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <PageHero
        eyebrow="Share your success"
        title="Tell Your Story"
        subtitle="Every positive action matters. Share how you, your community or your organization is making a difference for Puerto Rico's environment."
      />

      <section className="bg-white">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 py-20 grid lg:grid-cols-3 gap-12">
          <form onSubmit={submit} className="lg:col-span-2 space-y-12">
            {/* Honeypot: hidden from humans, bots tend to fill it */}
            <div className="absolute -left-[9999px] top-auto w-px h-px overflow-hidden" aria-hidden="true">
              <label htmlFor="website">Website</label>
              <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} />
            </div>
            {/* Author */}
            <div className="bg-[#F8F7F0] rounded-xl p-7 md:p-10 border border-[#ebe9df]">
              <h2 className="text-2xl md:text-3xl font-extrabold">Author Details</h2>
              <p className="text-[#878680] mt-3 leading-relaxed">
                Kindly provide your contact details to ensure proper credit attribution for this story. Be assured that we uphold the highest standards of privacy and do not share or sell your contact information.
              </p>
              <div className="grid sm:grid-cols-2 gap-5 mt-8">
                <div className="space-y-2">
                  <Label>First Name *</Label>
                  <Input className={inputCls} value={form.firstName} onChange={set("firstName")} placeholder="First" data-testid="first-name" />
                </div>
                <div className="space-y-2">
                  <Label>Last Name</Label>
                  <Input className={inputCls} value={form.lastName} onChange={set("lastName")} placeholder="Last" data-testid="last-name" />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label>Email *</Label>
                  <Input type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid="email" />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <div className="flex justify-between">
                    <Label>Short Author Bio</Label>
                    <span className={`text-xs ${form.bio.length > 300 ? "text-red-500" : "text-[#878680]"}`}>{form.bio.length}/300</span>
                  </div>
                  <Textarea rows={3} className="bg-white border-[#e4e2d6] focus-visible:ring-[#4BAF47] focus-visible:ring-offset-0 resize-none" value={form.bio} onChange={set("bio")} placeholder="Please keep it below 300 characters." />
                </div>
              </div>
            </div>

            {/* Story */}
            <div className="bg-[#F8F7F0] rounded-xl p-7 md:p-10 border border-[#ebe9df]">
              <h2 className="text-2xl md:text-3xl font-extrabold">Submit Your Story</h2>
              <p className="text-[#878680] mt-3">Please submit your story by using the fields below.</p>
              <div className="space-y-6 mt-8">
                <div className="space-y-2">
                  <Label>Title *</Label>
                  <Input className={inputCls} value={form.title} onChange={set("title")} placeholder="A short, inspiring headline" data-testid="story-title" />
                </div>
                <div className="space-y-2">
                  <Label>Story *</Label>
                  <Textarea rows={10} className="bg-white border-[#e4e2d6] focus-visible:ring-[#4BAF47] focus-visible:ring-offset-0" value={form.story} onChange={set("story")} placeholder="Tell us what happened, who was involved and the impact it had..." data-testid="story-content" />
                </div>

                <div className="space-y-2">
                  <Label>Featured Image</Label>
                  {image ? (
                    <div className="relative rounded-lg overflow-hidden border border-[#e4e2d6] bg-white">
                      <img src={image.dataUrl} alt="preview" className="w-full h-56 object-cover" />
                      <button type="button" onClick={() => setImage(null)} className="absolute top-3 right-3 h-9 w-9 rounded-full bg-white/90 text-[#24231D] flex items-center justify-center shadow hover:bg-white" aria-label="Remove image">
                        <X size={16} />
                      </button>
                      <p className="text-xs text-[#878680] px-4 py-2 truncate">{image.name}</p>
                    </div>
                  ) : (
                    <div
                      onClick={() => fileRef.current?.click()}
                      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                      onDragLeave={() => setDragging(false)}
                      onDrop={(e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files?.[0]); }}
                      className={`cursor-pointer rounded-lg border-2 border-dashed p-10 text-center bg-white transition-colors ${dragging ? "border-[#4BAF47] bg-[#4BAF47]/5" : "border-[#d9d7cb] hover:border-[#4BAF47]"}`}
                    >
                      <UploadCloud size={36} className="mx-auto text-[#4BAF47]" />
                      <p className="font-bold mt-3">Click or drag a file to this area to upload.</p>
                      <p className="text-sm text-[#878680] mt-1">Please make sure the dimensions are (600 x 300px). Max 5MB.</p>
                      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Excerpt</Label>
                  <Textarea rows={3} className="bg-white border-[#e4e2d6] focus-visible:ring-[#4BAF47] focus-visible:ring-offset-0 resize-none" value={form.excerpt} onChange={set("excerpt")} placeholder="A one or two sentence summary (optional)" />
                </div>

                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select value={form.category} onValueChange={set("category")}>
                    <SelectTrigger className={inputCls} data-testid="category-select">
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-4 pt-2">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <Checkbox checked={form.agreePrivacy} onCheckedChange={(v) => setForm({ ...form, agreePrivacy: !!v })} className="mt-0.5 data-[state=checked]:bg-[#4BAF47] data-[state=checked]:border-[#4BAF47]" data-testid="agree-privacy" />
                    <span className="text-sm leading-relaxed">I agree with EnvirojusticePR.org Privacy Policy *</span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <Checkbox checked={form.agreeFeature} onCheckedChange={(v) => setForm({ ...form, agreeFeature: !!v })} className="mt-0.5 data-[state=checked]:bg-[#4BAF47] data-[state=checked]:border-[#4BAF47]" data-testid="agree-feature" />
                    <span className="text-sm leading-relaxed text-[#5f5e58]">
                      I acknowledge that my story may be featured on the EnvirojusticePR.org website and social media channels. After the editorial team reviews and approves it, my story will be shared to inspire and contribute to environmental justice initiatives. *
                    </span>
                  </label>
                </div>

                <button type="submit" disabled={submitting} className="btn-green w-full sm:w-auto justify-center disabled:opacity-70 mt-2" data-testid="submit-story">
                  {submitting ? "Submitting..." : "Submit Story"} <ChevronsRight size={16} />
                </button>
                <p className="text-xs text-[#878680] mt-3">All stories are reviewed by our editorial team before publishing. Nothing goes live without approval.</p>
              </div>
            </div>
          </form>

          {/* Side info */}
          <aside className="space-y-6 lg:sticky lg:top-32 self-start">
            <div className="bg-[#24231D] text-white rounded-xl p-8">
              <ShieldCheck size={36} className="text-[#4BAF47]" />
              <h3 className="text-2xl font-extrabold mt-4">What happens next?</h3>
              <ol className="mt-5 space-y-4 text-[#c9c8c0] text-sm leading-relaxed list-none">
                {[
                  "Your submission is received and saved securely.",
                  "Our editorial team reviews it for accuracy and clarity.",
                  "Once approved it is published in Discover Stories.",
                  "We share it on our social channels to inspire others.",
                ].map((t, i) => (
                  <li key={t} className="flex gap-3">
                    <span className="h-6 w-6 rounded-full bg-[#4BAF47] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                    {t}
                  </li>
                ))}
              </ol>
            </div>
            <div className="bg-[#F8F7F0] rounded-xl p-8 border border-[#ebe9df]">
              <h3 className="text-xl font-extrabold">Eligible stories</h3>
              <p className="text-[#878680] text-sm mt-3 leading-relaxed">
                Environmental initiatives, sustainable practices, conservation efforts and positive impacts on nature. Community-led projects, individual achievements or corporate initiatives are all welcome.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
